import { useState, useMemo, useCallback } from "react";

import { ComboBox, type ComboBoxProps, type SelectedKey } from "../ComboBox";

import type { Key } from "react-aria-components";

interface AutocompleteProps<T extends SelectedKey> extends Omit<
  ComboBoxProps<T>,
  | "selectedItems"
  | "selectionMode"
  | "onItemInserted"
  | "onItemCleared"
  | "defaultValue"
  | "value"
  | "onChange"
> {
  value?: T | T[];
  defaultValue?: T | T[];
  onChange?: (value: T | T[] | null) => void;
  multiple?: boolean;
}

export function Autocomplete<T extends SelectedKey>({
  value: controlledValue,
  defaultValue,
  onChange,
  multiple = false,
  ...comboBoxProps
}: AutocompleteProps<T>) {
  const [internalValue, setInternalValue] = useState<T[]>(() => {
    if (defaultValue === undefined) return [];
    return Array.isArray(defaultValue) ? defaultValue : [defaultValue];
  });

  // When controlledValue is defined it takes precedence; otherwise fall back to internalValue.
  const selectedArray: T[] = useMemo(() => {
    if (controlledValue !== undefined) {
      return Array.isArray(controlledValue) ? controlledValue : [controlledValue];
    }
    return internalValue;
  }, [controlledValue, internalValue]);

  const handleChange = useCallback(
    (newValue: T[]) => {
      // Always update internalValue so it stays in sync.
      setInternalValue(newValue);
      onChange?.(multiple ? newValue : newValue.length > 0 ? newValue[0] : null);
    },
    [multiple, onChange],
  );

  const selectedItems = useMemo(
    () => ({
      items: selectedArray,
      append: (item: T) => {
        const newValue = multiple
          ? [...selectedArray.filter((i) => i.id !== item.id), item]
          : [item];
        handleChange(newValue);
      },
      appendMany: (items: T[]) => {
        if (!multiple) {
          handleChange(items.slice(-1));
          return;
        }
        const existing = new Set(selectedArray.map((i) => String(i.id)));
        const additions = items.filter((i) => !existing.has(String(i.id)));
        handleChange([...selectedArray, ...additions]);
      },
      remove: (...keys: Key[]) => {
        const keySet = new Set(keys.map(String));
        const newValue = selectedArray.filter((item) => !keySet.has(String(item.id)));
        handleChange(newValue);
      },
    }),
    [selectedArray, multiple, handleChange],
  );

  return (
    <ComboBox<T>
      {...comboBoxProps}
      selectedItems={selectedItems}
      selectionMode={multiple ? "multiple" : "single"}
    />
  );
}

Autocomplete.Tag = ComboBox.Tag;
Autocomplete.Option = ComboBox.Option;

export type { AutocompleteProps, SelectedKey };
